import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import { categories } from '@/mocks/categories';

export default function GuidaAcquisti() {
  return (
    <>
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="max-w-[960px] mx-auto px-6 md:px-10">

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Guida Acquisti Itaobuy Sheet: Come Comprare in Modo Intelligente",
              "description": "La guida definitiva per acquistare prodotti di qualità attraverso Itaobuy Spreadsheet. Scopri come navigare le categorie, confrontare i prezzi e fare acquisti sicuri.",
              "datePublished": "2026-07-14",
              "dateModified": "2026-07-14",
              "author": {
                "@type": "Organization",
                "name": "Itaobuy Spreadsheet Italia",
                "url": "https://itaobuyspread.site/"
              },
              "publisher": {
                "@type": "Organization",
                "name": "Itaobuy Spreadsheet Italia",
                "url": "https://itaobuyspread.site/",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg"
                }
              },
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://itaobuyspread.site/guida-acquisti/"
              },
              "inLanguage": "it-IT"
            })}
          </script>

          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to="/" className="hover:text-foreground-900 transition-colors">
                  Home
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Guida Acquisti</li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
              Guida Acquisti 2026
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
              Guida Acquisti Itaobuy Sheet: Come Comprare in Modo Intelligente
            </h1>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base max-w-2xl">
              La guida definitiva per acquistare prodotti di qualità attraverso Itaobuy Spreadsheet. 
              Scopri come navigare le categorie, confrontare i prezzi e fare acquisti sicuri.
            </p>
          </header>

          <div className="relative w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=Organized%20shopping%20desk%20scene%20with%20laptop%20open%20showing%20spreadsheet%2C%20fashion%20items%20arranged%20neatly%20around%2C%20warm%20cream%20background%2C%20minimal%20editorial%20photography%2C%20soft%20natural%20daylight%2C%20clean%20aesthetic%2C%20modern%20lifestyle%20composition&width=1400&height=600&seq=guida-hero-01&orientation=landscape"
              alt="Guida Acquisti Itaobuy Sheet"
              title="Guida Acquisti - Itaobuy Spreadsheet"
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-10">
            <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
              Indice dei Contenuti
            </h2>
            <nav className="flex flex-col gap-2">
              <a href="#iniziare" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                1. Come Iniziare con Itaobuy Sheet
              </a>
              <a href="#navigazione" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                2. Navigare tra le Categorie
              </a>
              <a href="#selezione" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                3. Come Selezionare i Prodotti Migliori
              </a>
              <a href="#qualita" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                4. Verificare la Qualità dei Prodotti
              </a>
              <a href="#acquisto" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                5. Completare l&apos;Acquisto in Sicurezza
              </a>
              <a href="#strategie" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                6. Strategie Avanzate di Acquisto
              </a>
              <a href="#categorie-guida" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                7. Guida alle Categorie di Prodotto
              </a>
              <a href="#faq-acquisti" className="text-sm text-foreground-600 hover:text-primary-500 transition-colors">
                8. FAQ Acquisti
              </a>
            </nav>
          </div>

          <div className="space-y-8 text-foreground-700 leading-relaxed text-sm md:text-base">
            <section id="iniziare">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                1. Come Iniziare con Itaobuy Sheet
              </h2>
              <p>
                Iniziare a utilizzare <strong>Itaobuy Spreadsheet</strong> è semplice e immediato. 
                Non è richiesta alcuna registrazione: puoi accedere direttamente alla piattaforma 
                e iniziare subito a esplorare il catalogo prodotti. Ecco i primi passi da seguire:
              </p>
              <ol className="list-decimal pl-5 space-y-2 my-4">
                <li>Visita la <Link to="/" className="text-primary-500 hover:text-primary-600 underline font-medium">homepage</Link> di Itaobuy Spreadsheet Italia</li>
                <li>Esplora le categorie disponibili nel menu a tendina o nella griglia centrale</li>
                <li>Scegli la categoria che ti interessa e clicca per accedere al catalogo</li>
                <li>Utilizza i filtri e l&apos;ordinamento per trovare esattamente ciò che cerchi</li>
                <li>Una volta trovato il prodotto desiderato, clicca sul link verificato per procedere</li>
              </ol>
              <p>
                Non serve scaricare nulla né installare applicazioni: Itaobuy Spreadsheet funziona 
                direttamente dal tuo browser, su qualsiasi dispositivo.
              </p>
            </section>

            <section id="navigazione">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                2. Navigare tra le Categorie
              </h2>
              <p>
                La navigazione di Itaobuy Spreadsheet è stata progettata per essere intuitiva 
                ed efficiente. Il <strong>menu a tendina &quot;Categorie&quot;</strong> nella barra 
                superiore ti permette di accedere rapidamente a tutte le 14 categorie di prodotti. 
                In alternativa, puoi utilizzare la griglia di categorie nella homepage per 
                un&apos;esperienza più visiva.
              </p>
              <p>
                Ogni categoria è organizzata in modo logico: dall&apos;abbigliamento agli accessori, 
                dalle calzature ai profumi. La struttura a <strong>fogli di calcolo</strong> ti 
                consente di visualizzare tutti i prodotti contemporaneamente, facilitando il 
                confronto e la scelta.
              </p>
            </section>

            <section id="selezione">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                3. Come Selezionare i Prodotti Migliori
              </h2>
              <p>
                Selezionare il prodotto giusto tra centinaia di opzioni può sembrare complesso, 
                ma con Itaobuy Spreadsheet il processo è semplificato grazie a diversi strumenti:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <div className="bg-accent-50 rounded-xl p-5 border border-accent-200/70">
                  <h3 className="font-semibold text-foreground-900 mb-1">QC Photos</h3>
                  <p className="text-foreground-600 text-xs">
                    Le immagini di controllo qualità mostrano il prodotto reale. Confrontale 
                    con le foto promozionali per verificare la corrispondenza.
                  </p>
                </div>
                <div className="bg-accent-50 rounded-xl p-5 border border-accent-200/70">
                  <h3 className="font-semibold text-foreground-900 mb-1">Descrizioni Complete</h3>
                  <p className="text-foreground-600 text-xs">
                    Materiali, dimensioni, colori: ogni dettaglio è importante. Leggi sempre 
                    la descrizione completa prima di decidere.
                  </p>
                </div>
                <div className="bg-accent-50 rounded-xl p-5 border border-accent-200/70">
                  <h3 className="font-semibold text-foreground-900 mb-1">Confronto Prezzi</h3>
                  <p className="text-foreground-600 text-xs">
                    La visualizzazione a foglio di calcolo permette di confrontare i prezzi 
                    di prodotti simili con un solo sguardo.
                  </p>
                </div>
                <div className="bg-accent-50 rounded-xl p-5 border border-accent-200/70">
                  <h3 className="font-semibold text-foreground-900 mb-1">Recensioni</h3>
                  <p className="text-foreground-600 text-xs">
                    Quando disponibili, leggi le recensioni di altri acquirenti per farti 
                    un&apos;idea più completa della qualità del prodotto.
                  </p>
                </div>
              </div>
            </section>

            <section id="qualita">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                4. Verificare la Qualità dei Prodotti
              </h2>
              <p>
                La verifica della qualità è uno degli aspetti più importanti dello shopping 
                online internazionale. Itaobuy Spreadsheet offre diversi strumenti per 
                assicurarti di ricevere esattamente ciò che ti aspetti:
              </p>
              <ul className="list-disc pl-5 space-y-2 my-4">
                <li>
                  <strong>Confronta le QC photos con le immagini promozionali</strong>: 
                  differenze significative potrebbero indicare discrepanze nella qualità.
                </li>
                <li>
                  <strong>Controlla i materiali indicati</strong>: cotone, poliestere, pelle 
                  — verifica che corrispondano alle tue aspettative.
                </li>
                <li>
                  <strong>Verifica le dimensioni</strong>: le taglie possono variare tra 
                  mercati diversi; consulta sempre la guida alle taglie.
                </li>
                <li>
                  <strong>Leggi le politiche di reso</strong>: sapere in anticipo se puoi 
                  restituire un prodotto ti dà maggiore tranquillità.
                </li>
              </ul>
            </section>

            <section id="acquisto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                5. Completare l&apos;Acquisto in Sicurezza
              </h2>
              <p>
                Una volta scelto il prodotto, ecco come completare l&apos;acquisto in tutta sicurezza:
              </p>
              <div className="space-y-4 my-6">
                {[
                  { num: 1, title: 'Usa il link verificato', text: 'Clicca sul link del prodotto fornito da Itaobuy Spreadsheet. È stato verificato e garantito come sicuro.' },
                  { num: 2, title: 'Controlla i dettagli finali', text: 'Nella pagina del marketplace, verifica che il prodotto, il prezzo e le condizioni corrispondano a quanto visto su Itaobuy Spreadsheet.' },
                  { num: 3, title: 'Scegli il metodo di pagamento', text: 'Usa metodi di pagamento sicuri come carte di credito con protezione acquirente o servizi di pagamento affidabili.' },
                  { num: 4, title: 'Verifica le spese di spedizione', text: 'Controlla i costi e i tempi di spedizione stimati prima di finalizzare l\'ordine.' },
                  { num: 5, title: 'Salva la conferma', text: 'Conserva sempre la conferma d\'ordine e il numero di tracking per monitorare la spedizione.' },
                ].map((item) => (
                  <div key={item.num} className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-xs font-bold">
                      {item.num}
                    </span>
                    <div>
                      <strong className="text-foreground-900">{item.title}</strong>
                      <p className="text-foreground-600 text-sm mt-0.5">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section id="strategie">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                6. Strategie Avanzate di Acquisto
              </h2>
              <p>
                Per gli acquirenti più esperti, ecco alcune <strong>strategie avanzate</strong> 
                per ottenere il massimo da Itaobuy Spreadsheet:
              </p>
              <div className="space-y-4 my-6">
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-900 mb-1">Acquisti Stagionali</h3>
                  <p className="text-foreground-600 text-sm">
                    Pianifica i tuoi acquisti in base alle stagioni. I prezzi di abbigliamento 
                    e accessori variano significativamente durante l&apos;anno. Acquista capi 
                    invernali in estate e viceversa per ottenere i prezzi migliori.
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-900 mb-1">Acquisti in Blocco</h3>
                  <p className="text-foreground-600 text-sm">
                    Alcuni venditori offrono sconti per acquisti multipli. Se trovi un prodotto 
                    che ti piace in diversi colori, considera l&apos;acquisto in blocco per risparmiare 
                    sulle spese di spedizione.
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-5">
                  <h3 className="font-semibold text-foreground-900 mb-1">Monitoraggio Prezzi</h3>
                  <p className="text-foreground-600 text-sm">
                    Tieni traccia dei prezzi dei prodotti che ti interessano. Itaobuy Spreadsheet 
                    aggiorna regolarmente il catalogo, quindi potresti trovare offerte migliori 
                    in momenti diversi.
                  </p>
                </div>
              </div>
            </section>

            <section id="categorie-guida">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                7. Guida alle Categorie di Prodotto
              </h2>
              <p>
                Ogni categoria su Itaobuy Spreadsheet ha le sue particolarità. Ecco una breve 
                panoramica per aiutarti a orientarti:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-6">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors text-sm font-medium whitespace-nowrap"
                  >
                    <i className="ri-arrow-right-up-line text-accent-500"></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            </section>

            <section id="faq-acquisti">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                8. FAQ Acquisti
              </h2>
              <div className="space-y-3">
                <details className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-medium text-foreground-900 text-sm list-none">
                    Devo creare un account per usare Itaobuy Sheet?
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm">
                    <p>
                      No, non è necessario creare alcun account. Puoi accedere liberamente 
                      a tutte le funzionalità e categorie senza registrazione.
                    </p>
                  </div>
                </details>
                <details className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-medium text-foreground-900 text-sm list-none">
                    Quanto tempo impiega la spedizione?
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm">
                    <p>
                      I tempi di spedizione variano in base al venditore e alla destinazione. 
                      Controlla sempre le informazioni sulla spedizione nella pagina del prodotto 
                      prima di acquistare.
                    </p>
                  </div>
                </details>
                <details className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-medium text-foreground-900 text-sm list-none">
                    Posso restituire un prodotto se non mi piace?
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm">
                    <p>
                      Le politiche di reso dipendono dal venditore. Verifica sempre le condizioni 
                      di reso nella pagina del marketplace prima di completare l&apos;acquisto. Per 
                      ulteriori domande, visita la nostra <Link to="/" className="text-primary-500 hover:text-primary-600 underline">homepage</Link>.
                    </p>
                  </div>
                </details>
                <details className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer font-medium text-foreground-900 text-sm list-none">
                    Come faccio a sapere se un prodotto è di buona qualità?
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                  </summary>
                  <div className="px-5 pb-5 text-foreground-600 text-sm">
                    <p>
                      Utilizza le QC photos per vedere il prodotto reale, leggi le descrizioni 
                      dei materiali e, quando disponibili, consulta le recensioni di altri 
                      acquirenti. Questi tre elementi ti daranno un quadro completo della qualità.
                    </p>
                  </div>
                </details>
              </div>
            </section>
          </div>

          <div className="mt-12 p-6 md:p-8 bg-accent-50 rounded-2xl border border-accent-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <div>
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-1">
                  Pronto per lo Shopping?
                </h3>
                <p className="text-foreground-600 text-sm">
                  Metti in pratica questa guida e inizia subito i tuoi acquisti.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center px-5 py-2.5 rounded-full bg-background-50 text-foreground-900 text-sm font-semibold border border-background-200 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-4-line mr-2"></i>
                  Torna alla Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  buy it now
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>
      <FooterSection />
    </>
  );
}